import { EncapsulatedFeatureKeys } from "@lyfegen-platform/graphql-schema/models";
import { LyfeTooltip } from "@lyfegen-platform/react-common";
import {
	Badge,
	Box,
	ClickAwayListener,
	Divider,
	Grow,
	IconButton,
	Link,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Tab,
	Tabs,
	Typography,
	useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import EncapsulatedFeature from "../../components/icons/EncapsulatedFeature";
import ExpandLess from "../../components/icons/ExpandLess";
import ExpandMore from "../../components/icons/ExpandMore";
import HelpIcon from "../../components/icons/HelpIcon";
import LogoutIcon from "../../components/icons/LogoutIcon";
import { OrganizationSettingsIcon, ProfileSettingsIcon, TermsPoliciesIcon, UptimeItemIcon } from "../../components/icons/ProfileSettings";
import { supportColor } from "../../hooks/use-map-theme-styles";
import { ServiceModulesEnum, supportLink, supportLinkWithProtocol } from "../../models/common/modules";
import { Role } from "../../models/identity/role";
import { notificationInfoState, questState, toggleMenu, userOrganization } from "../../store/layout";
import { userPersonalInfoState } from "../../store/user";
import QuestsTabPanel from "./inbox/quests/QuestTablePanel";
import TasksTabPanel from "./inbox/tasks/TasksTablePanel";
import layoutStyles from "./layout-styles";

export interface MenuProps {
	handleLogout: () => void;
	identityServerUrl: string;
}

const Item = (props: { id?: string; text: string; onClick?: () => void; icon?: JSX.Element }) => {
	const { menuItem } = layoutStyles();

	return (
		<MenuItem id={props.id} className={menuItem} onClick={props.onClick}>
			{props.icon && props.icon}
			&nbsp;&nbsp;
			<Typography noWrap>{props.text}</Typography>
		</MenuItem>
	);
};

const UptimeItem = () => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { itemLink } = layoutStyles();

	return (
		<Link href={"https://lyfegen-health-checks.azurewebsites.net/client-public"} target="_blank" className={itemLink}>
			<Item text={t("uptime-status")} icon={<UptimeItemIcon color={palette.primary.main} />} />
		</Link>
	);
};

const SettingsItem = (props: { onClick: () => void }) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { push } = useHistory();

	return (
		<Item
			onClick={() => {
				props.onClick();
				push("/organization-settings");
			}}
			text={t("organization-settings")}
			icon={<OrganizationSettingsIcon color={palette.primary.main} />}
		/>
	);
};

const ProfileItem = (props: { onClick: () => void }) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { push } = useHistory();

	return (
		<Item
			onClick={() => {
				props.onClick();
				push("/profile-settings");
			}}
			text={t("profile-settings")}
			icon={<ProfileSettingsIcon color={palette.primary.main} />}
		/>
	);
};

const TermsAndPoliciesItem = ({ identityServerUrl }: { identityServerUrl: string }) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { itemLink, menuItem } = layoutStyles();
	const url = new URL("/legal", identityServerUrl);

	return (
		<Link href={url.href} target="_blank" className={itemLink}>
			<MenuItem className={menuItem}>
				<TermsPoliciesIcon color={palette.primary.main} />
				&nbsp;&nbsp;
				<Typography noWrap>{t("terms-and-policies")}</Typography>
			</MenuItem>
		</Link>
	);
};

const Menu: React.FC<MenuProps> = ({ handleLogout, identityServerUrl }) => {
	const { t } = useTranslation();
	const { spacing, palette } = useTheme();
	const anchorRef = useRef<HTMLButtonElement>(null);
	const openTaskRef = useRef<HTMLButtonElement>(null);
	const user = useRecoilValue(userPersonalInfoState);
	const [taskBadgeCount, setTaskBadgeCount] = useState(user.notificationCount);
	const [questBadgeCount, setQuestBadgeCount] = useState(user.questsCount);
	const setExpendedQuest = useSetRecoilState(questState);
	const organization = useRecoilValue(userOrganization);
	const areQuestsEnabledByTier = organization.encapsulatedFeatures.some(
		(feature) => feature.isEnabled && feature.ofType.key === EncapsulatedFeatureKeys.Quests
	);
	const [openNotificationPopup, setOpenNotificationPopup] = useRecoilState(notificationInfoState);
	const [open, setOpen] = useRecoilState(toggleMenu);
	const [tabValue, setTabValue] = useState(0);
	const { inboxBadge, inboxBadgeTopRight, inboxWindow, closeInboxButton, inboxTab, popperRoot, menuList, activeTab } = layoutStyles();
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleOrganizationSettingsToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleOpenNotificationInfoDialog = (e: any) => {
		setOpenNotificationPopup((prevOpen: boolean) => !prevOpen);
	};
	const handleClose = (event: React.MouseEvent<EventTarget>) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}
		setOpen(false);
	};

	useEffect(() => {
		if (!openNotificationPopup) {
			setExpendedQuest((prev) => {
				return { questId: "" };
			});
		}
	}, [openNotificationPopup]);

	useEffect(() => {
		if (taskBadgeCount !== user.notificationCount) {
			setTaskBadgeCount(user.notificationCount || 0);
		}

		if (questBadgeCount !== user.questsCount) {
			setQuestBadgeCount(user.questsCount || 0);
		}
	}, [user]);

	useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current!.focus();
		}

		prevOpen.current = open;
	}, [open]);

	const handleListKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Tab") {
			event.preventDefault();
			setOpen(false);
		}
	};
	const prevOpen = useRef(open);
	const isSupportAvailable = user.modules.some((m) => m.code === ServiceModulesEnum.support);

	const handleChange = (event: React.ChangeEvent<Record<string, unknown>>, newTabValue: number) => setTabValue(newTabValue);

	return (
		<>
			<IconButton
				id="navbar-menu"
				style={{ marginLeft: spacing(2), marginRight: spacing(1), width: "40px", height: "40px" }}
				ref={anchorRef}
				onClick={handleToggle}
				size="small"
			>
				{open ? <ExpandMore /> : <ExpandLess />}
			</IconButton>
			<Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps}>
						<Paper style={{ marginTop: spacing(1) }}>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown} className={menuList}>
									<ProfileItem onClick={handleToggle} />
									{user.role === Role.Administrator && <SettingsItem onClick={handleOrganizationSettingsToggle} />}
									<Divider color={palette.tableDivider.light} style={{ margin: "16px 0" }} />
									<TermsAndPoliciesItem identityServerUrl={identityServerUrl} />
									<UptimeItem />
									<Divider color={palette.tableDivider.light} style={{ margin: "16px 0" }} />
									<Item
										id="logout"
										onClick={handleLogout}
										text={t("logout")}
										icon={<LogoutIcon color={palette.primary.main} />}
									/>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
			<IconButton ref={openTaskRef} onClick={(e) => handleOpenNotificationInfoDialog(e)}>
				<Badge className={inboxBadge} badgeContent={taskBadgeCount + questBadgeCount} color="primary">
					<NotificationsIcon color="primary" />
				</Badge>
			</IconButton>
			{isSupportAvailable && (
				<IconButton href="https://support.lyfegen.com/hc/de" target="_blank" rel="noopener noreferrer">
					<HelpIcon color={palette.primary.main} />
				</IconButton>
			)}
			{openNotificationPopup && (
				<Box className={popperRoot}>
					<ClickAwayListener
						mouseEvent={"onMouseUp"}
						onClickAway={(e) => {
							if (!openTaskRef.current?.contains(e.target as Node)) {
								setOpenNotificationPopup(false);
							}
						}}
					>
						<Paper className={inboxWindow}>
							<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
								<Tabs value={tabValue} onChange={handleChange}>
									<Tab
										style={{ height: 32 }}
										className={inboxTab}
										label={
											<Badge
												className={`${inboxBadge} ${inboxBadgeTopRight} ${tabValue === 0 && activeTab}`}
												badgeContent={taskBadgeCount}
												color="primary"
											>
												{t("tasks")}
											</Badge>
										}
									/>
									<Tab
										style={{ height: 32, pointerEvents: "auto" }}
										className={inboxTab}
										disabled={!areQuestsEnabledByTier}
										label={
											areQuestsEnabledByTier ? (
												<Badge
													className={`${inboxBadge} ${inboxBadgeTopRight} ${tabValue === 1 && activeTab}`}
													badgeContent={questBadgeCount}
													color="primary"
												>
													{t("quests")}
												</Badge>
											) : (
												<LyfeTooltip
													placement="right-start"
													interactive
													title={
														<Typography variant="body1">
															{t("quests-premium-feature")}
															<Link
																style={{ color: supportColor, cursor: "pointer" }}
																href={supportLinkWithProtocol}
															>
																{supportLink}
															</Link>
														</Typography>
													}
												>
													<Badge
														className={`${inboxBadge} ${inboxBadgeTopRight} ${tabValue === 1 && activeTab}`}
														badgeContent={0}
														color="primary"
													>
														<Box display="flex" alignItems="center">
															<Box mr={1}>{t("quests")}</Box> <EncapsulatedFeature />
														</Box>
													</Badge>
												</LyfeTooltip>
											)
										}
									/>
								</Tabs>
							</Box>
							<TasksTabPanel value={tabValue} index={0} />
							<QuestsTabPanel value={tabValue} index={1} />

							<IconButton className={closeInboxButton} onClick={() => setOpenNotificationPopup(false)}>
								<CloseIcon />
							</IconButton>
						</Paper>
					</ClickAwayListener>
				</Box>
			)}
		</>
	);
};

export default Menu;
